import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Card } from '@/hooks/useCards';

export type Deck = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  ink_1: string;
  ink_2: string;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export type DeckCard = {
  id: string;
  deck_id: string;
  card_id: string;
  quantity: number;
  sideboard: boolean;
  notes: string | null;
  created_at: string;
  card?: Card;
};

export type DeckWithCards = Deck & { cards: DeckCard[]; total_cards: number };

export function useDecks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['decks', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('decks')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      if (error) throw error;

      // Get card counts per deck
      const deckIds = (data || []).map(d => d.id);
      if (!deckIds.length) return [];
      const { data: deckCards } = await supabase
        .from('deck_cards')
        .select('deck_id, quantity')
        .in('deck_id', deckIds);

      const countMap = new Map<string, number>();
      (deckCards || []).forEach(dc => {
        countMap.set(dc.deck_id, (countMap.get(dc.deck_id) || 0) + dc.quantity);
      });

      return (data || []).map(d => ({
        ...d,
        cards: [],
        total_cards: countMap.get(d.id) || 0,
      })) as DeckWithCards[];
    },
    enabled: !!user,
  });
}

export function useDeckCards(deckId: string | null) {
  return useQuery({
    queryKey: ['deck-cards', deckId],
    queryFn: async () => {
      if (!deckId) return [];
      const { data, error } = await supabase
        .from('deck_cards')
        .select('*, card:cards(*)')
        .eq('deck_id', deckId)
        .order('created_at');
      if (error) throw error;
      return (data || []) as DeckCard[];
    },
    enabled: !!deckId,
  });
}

export function useCreateDeck() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description, ink_1, ink_2 }: { name: string; description?: string; ink_1: string; ink_2: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('decks')
        .insert({ user_id: user.id, name, description: description || null, ink_1: ink_1 as any, ink_2: ink_2 as any })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decks'] }),
  });
}

export function useUpdateDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name: string; description?: string }) => {
      const { error } = await supabase
        .from('decks')
        .update({ name, description: description || null })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decks'] }),
  });
}

export function useDeleteDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('decks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decks'] }),
  });
}

export function useAddCardToDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckId, cardId, quantity }: { deckId: string; cardId: string; quantity?: number }) => {
      // Check if card already in deck
      const { data: existing } = await supabase
        .from('deck_cards')
        .select('id, quantity')
        .eq('deck_id', deckId)
        .eq('card_id', cardId)
        .maybeSingle();

      if (existing) {
        const newQty = existing.quantity + (quantity || 1);
        if (newQty > 4) throw new Error('Maximum 4 copies per card');
        const { error } = await supabase.from('deck_cards').update({ quantity: newQty }).eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('deck_cards').insert({ deck_id: deckId, card_id: cardId, quantity: quantity || 1 });
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
      qc.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

export function useRemoveCardFromDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckId, cardId, removeAll }: { deckId: string; cardId: string; removeAll?: boolean }) => {
      if (removeAll) {
        const { error } = await supabase.from('deck_cards').delete().eq('deck_id', deckId).eq('card_id', cardId);
        if (error) throw error;
      } else {
        const { data: existing } = await supabase
          .from('deck_cards')
          .select('id, quantity')
          .eq('deck_id', deckId)
          .eq('card_id', cardId)
          .maybeSingle();
        if (!existing) return;
        if (existing.quantity <= 1) {
          const { error } = await supabase.from('deck_cards').delete().eq('id', existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('deck_cards').update({ quantity: existing.quantity - 1 }).eq('id', existing.id);
          if (error) throw error;
        }
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
      qc.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

/** Set an exact quantity (1-4) for a card in a deck, inserting if missing. */
export function useSetDeckCardQty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckId, cardId, quantity }: { deckId: string; cardId: string; quantity: number }) => {
      const qty = Math.max(0, Math.min(4, quantity));
      const { data: existing } = await supabase
        .from('deck_cards')
        .select('id')
        .eq('deck_id', deckId)
        .eq('card_id', cardId)
        .maybeSingle();
      if (qty === 0) {
        if (existing) {
          const { error } = await supabase.from('deck_cards').delete().eq('id', existing.id);
          if (error) throw error;
        }
        return;
      }
      if (existing) {
        const { error } = await supabase.from('deck_cards').update({ quantity: qty }).eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('deck_cards').insert({ deck_id: deckId, card_id: cardId, quantity: qty });
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
      qc.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

/** Toggle a card between the main deck and the sideboard. */
export function useToggleSideboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckCardId, sideboard }: { deckCardId: string; sideboard: boolean; deckId: string }) => {
      const { error } = await supabase.from('deck_cards').update({ sideboard }).eq('id', deckCardId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
    },
  });
}

/** Save inline notes on a specific deck_card row. */
export function useUpdateDeckCardNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckCardId, notes }: { deckCardId: string; notes: string | null; deckId: string }) => {
      const { error } = await supabase.from('deck_cards').update({ notes }).eq('id', deckCardId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
    },
  });
}
